import { useEffect, useState, useMemo } from 'react';
import { FAIcon, Theme, useTranslation, type FAIconProps } from '@nextgen-web-framework/all';
import { PasswordStrengthMeterProps } from './types/propTypes';
import styles from './PasswordStrengthMeter.module.scss';
import { calculatePasswordStrength, PasswordStrengthLevel, PasswordStrengthSuggestion } from './PasswordStrength';
import { usePasswordDictionaries } from './usePasswordDictionaries';

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  onPasswordMeetsMinimumChange,
  isFocused = false,
  isPasswordHistoryError,
}: PasswordStrengthMeterProps) => {
  // calling hook to ensure dictionaries are loaded and ready for component
  const dictionariesLoaded = usePasswordDictionaries();
  const t = useTranslation();

  // Precompute translations.
  const translations = useMemo(
    () => ({
      weak: t('identity.PasswordStrength.weak'),
      medium: t('identity.PasswordStrength.medium'),
      strong: t('identity.PasswordStrength.strong'),
      suggestionChooseStronger: t('identity.PasswordStrength.Suggestion.chooseStronger'),
      suggestionCreatePasswordError: t('identity.CreatePasswordForm.createPasswordError'),
      suggestionTryCombination: t('identity.PasswordStrength.Suggestion.tryCombination'),
      suggestionChooseLessCommon: t('identity.PasswordStrength.Suggestion.chooseLessCommon'),
      suggestionPasswordHistory: t('identity.PasswordStrength.Suggestion.usedRecently'),
      suggestionSequence: t('identity.PasswordStrength.Suggestion.sequence'),
      suggestionRepeated: t('identity.PasswordStrength.Suggestion.simpleRepeat'),
      suggestionRepeatedExtended: t('identity.PasswordStrength.Suggestion.repeatedExtended'),
      suggestionStraightRowKeys: t('identity.PasswordStrength.Suggestion.straightRowKeys'),
      suggestionIsStrong: t('identity.PasswordStrength.Suggestion.isStrong'),
      label: t('identity.PasswordStrength.label'),
    }),
    [t],
  );

  const [userHasInteracted, setUserHasInteracted] = useState(false);

  useEffect(() => {
    if (isFocused && !userHasInteracted) {
      setUserHasInteracted(true);
    }
  }, [isFocused, userHasInteracted]);

  const isPasswordFieldEmpty = password.length === 0;
  const showValidation = userHasInteracted && !isPasswordFieldEmpty;
  const passwordHistoryErrorResult = {
    suggestions: PasswordStrengthSuggestion.HISTORY,
    passwordStrengthLevel: PasswordStrengthLevel.WEAK,
  };

  const passwordStrengthResult = useMemo(() => {
    if (isPasswordHistoryError) {
      return passwordHistoryErrorResult;
    }
    // before dictionaries arrive, default to WEAK
    if (!dictionariesLoaded) {
      return { suggestions: [], passwordStrengthLevel: PasswordStrengthLevel.WEAK };
    }
    return calculatePasswordStrength(password);
  }, [password, isPasswordHistoryError, dictionariesLoaded]);

  let passwordStrengthDisplayMessage = translations.weak;
  if (passwordStrengthResult.passwordStrengthLevel === PasswordStrengthLevel.MEDIUM) {
    passwordStrengthDisplayMessage = translations.medium;
  } else if (passwordStrengthResult.passwordStrengthLevel === PasswordStrengthLevel.STRONG) {
    passwordStrengthDisplayMessage = translations.strong;
  }

  const passwordStrengthSuggestions = isPasswordHistoryError
    ? PasswordStrengthSuggestion.HISTORY
    : passwordStrengthResult.suggestions;
  let suggestionsToDisplay = isPasswordHistoryError
    ? translations.suggestionPasswordHistory
    : translations.suggestionChooseStronger;

  for (const suggestion of passwordStrengthSuggestions) {
    switch (suggestion) {
      case PasswordStrengthSuggestion.HISTORY:
        suggestionsToDisplay = translations.suggestionPasswordHistory;
        break;
      case PasswordStrengthSuggestion.LENGTH:
        suggestionsToDisplay = translations.suggestionCreatePasswordError;
        break;
      case PasswordStrengthSuggestion.MEDIUM:
        suggestionsToDisplay = translations.suggestionTryCombination;
        break;
      case PasswordStrengthSuggestion.COMMON:
        suggestionsToDisplay = translations.suggestionChooseLessCommon;
        break;
      case PasswordStrengthSuggestion.STRONG:
        suggestionsToDisplay = translations.suggestionIsStrong;
        break;
      case PasswordStrengthSuggestion.REPEAT_EXTENDED:
        suggestionsToDisplay = translations.suggestionRepeatedExtended;
        break;
      case PasswordStrengthSuggestion.SEQUENCE:
        suggestionsToDisplay = translations.suggestionSequence;
        break;
      case PasswordStrengthSuggestion.SIMPLE_REPEAT:
        suggestionsToDisplay = translations.suggestionRepeated;
        break;
      case PasswordStrengthSuggestion.STRAIGHT_ROW:
        suggestionsToDisplay = translations.suggestionStraightRowKeys;
        break;
      default:
        break;
    }
  }

  const passwordMeetsMinimum = passwordStrengthResult.passwordStrengthLevel >= PasswordStrengthLevel.MEDIUM;

  useEffect(() => {
    onPasswordMeetsMinimumChange?.(passwordMeetsMinimum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordMeetsMinimum]);

  const barStyles = ['', '', ''];
  switch (passwordStrengthResult.passwordStrengthLevel) {
    case PasswordStrengthLevel.WEAK:
      barStyles[0] = styles.weak;
      break;
    case PasswordStrengthLevel.MEDIUM:
      barStyles[0] = styles.medium;
      barStyles[1] = styles.medium;
      break;
    case PasswordStrengthLevel.STRONG:
      barStyles[0] = styles.strong;
      barStyles[1] = styles.strong;
      barStyles[2] = styles.strong;
      break;
    default:
      break;
  }

  let iconProps: FAIconProps = {
    iconName: 'square-exclamation',
    theme: Theme.ERROR,
  };
  let suggestionsStyle = styles.weak;
  let passwordStrengthStyle = styles.weak;
  if (passwordStrengthResult.passwordStrengthLevel === PasswordStrengthLevel.MEDIUM) {
    passwordStrengthStyle = styles.medium;
    suggestionsStyle = styles.medium;
    iconProps = { iconName: 'circle-info', theme: Theme.ORANGE };
  } else if (passwordStrengthResult.passwordStrengthLevel === PasswordStrengthLevel.STRONG) {
    passwordStrengthStyle = styles.strong;
    suggestionsStyle = styles.strong;
    iconProps = { iconName: 'circle-check', theme: Theme.SUCCESS };
  } else if (isPasswordHistoryError) {
    passwordStrengthStyle = styles.weak;
    suggestionsStyle = styles.weak;
    iconProps = { iconName: 'square-exclamation', theme: Theme.ERROR };
  }

  return showValidation ? (
    <div className={styles['password-strength-meter']} data-testid="password-strength-meter">
      {/* The 3-bar visual */}
      <div className={styles['password-strength-meter__bar']}>
        <div className={barStyles[0]}></div>
        <div className={barStyles[1]}></div>
        <div className={barStyles[2]}></div>
      </div>

      {/* Label + Strength Message */}
      <div className={styles['password-strength-meter__label']}>
        {translations.label}
        <span
          className={`${styles['password-strength-meter__label-value']} ${passwordStrengthStyle}`}
          data-testid="password-strength-meter-message"
        >
          {passwordStrengthDisplayMessage}
        </span>
      </div>

      {/* Suggestions */}
      <div className={`${styles['password-strength-meter__suggestions']} ${suggestionsStyle}`}>
        <FAIcon {...iconProps} className="password-strength-meter__icon" />
        &nbsp;
        <div
          className={`${styles['password-strength-meter__suggestions__list']} ${suggestionsStyle}`}
          data-testid="password-strength-meter-suggestions"
        >
          {suggestionsToDisplay}
        </div>
      </div>
    </div>
  ) : null;
};

export default PasswordStrengthMeter;
